import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Calendar, Download, Building2, Image } from 'lucide-react';
import { getMosques } from '../api';

const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';

export default function Timetables() {
  const [mosques, setMosques] = useState([]);
  const [selectedMosque, setSelectedMosque] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    getMosques()
      .then((res) => {
        setMosques(res.data);
        if (res.data.length > 0) setSelectedMosque(res.data[0]);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = (mosque) => {
    setSelectedMosque(mosque);
    setDropdownOpen(false);
  };

  return (
    <div className="min-h-screen pt-24 pb-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 badge-emerald mb-4">
            <Calendar className="w-4 h-4" />
            Monthly Timetables
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Prayer <span className="gradient-text">Timetables</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            View and download monthly prayer timetables for any mosque in Oldham
          </p>
        </motion.div>

        {/* Mosque Selector */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <label className="block text-sm font-medium text-emerald-400 mb-2">Select Mosque</label>
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-between px-4 py-4 rounded-xl glass-card-light hover:border-emerald-500/40 border border-white/10 transition-all text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-emerald-400" />
                </div>
                <span className="text-white font-medium">
                  {selectedMosque ? selectedMosque.name : 'Choose a mosque...'}
                </span>
              </div>
              <motion.div animate={{ rotate: dropdownOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown className="w-5 h-5 text-gray-400" />
              </motion.div>
            </button>

            <AnimatePresence>
              {dropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full left-0 right-0 mt-2 glass-card rounded-xl overflow-hidden z-30 shadow-2xl border border-white/10"
                >
                  {loading ? (
                    <div className="p-4 text-gray-400 text-sm text-center">Loading mosques...</div>
                  ) : (
                    mosques.map((mosque) => (
                      <button
                        key={mosque._id}
                        onClick={() => handleSelect(mosque)}
                        className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-all ${
                          selectedMosque?._id === mosque._id
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : 'text-gray-300 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        <div className={`w-2 h-2 rounded-full ${selectedMosque?._id === mosque._id ? 'bg-emerald-400' : 'bg-gray-600'}`} />
                        <div>
                          <div className="font-medium">{mosque.name}</div>
                          <div className="text-xs text-gray-500">{mosque.address}</div>
                        </div>
                      </button>
                    ))
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Timetable Display */}
        {selectedMosque && (
          <motion.div
            key={selectedMosque._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {/* Mosque Info Header */}
            <div className="glass-card rounded-2xl p-6 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-white">{selectedMosque.name}</h2>
                  <p className="text-gray-400 text-sm mt-1">{selectedMosque.address}</p>
                </div>
                <div className="flex gap-3">
                  {selectedMosque.timetablePdfUrl && (
                    <a
                      href={`${BASE_URL}${selectedMosque.timetablePdfUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download PDF
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Timetable Image */}
            {selectedMosque.timetableImageUrl ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="glass-card rounded-2xl overflow-hidden"
              >
                <div className="bg-emerald-500/10 px-6 py-4 border-b border-white/5 flex items-center gap-2">
                  <Image className="w-5 h-5 text-emerald-400" />
                  <span className="text-emerald-400 font-medium">Prayer Timetable</span>
                </div>
                <div className="p-4">
                  <img
                    src={`${BASE_URL}${selectedMosque.timetableImageUrl}`}
                    alt={`${selectedMosque.name} timetable`}
                    className="w-full h-auto rounded-xl"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="hidden items-center justify-center py-16 text-center">
                    <div>
                      <Image className="w-16 h-16 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-400">Timetable image not available</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <div className="glass-card rounded-2xl flex flex-col items-center justify-center py-20 text-center">
                <div className="w-20 h-20 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-4">
                  <Calendar className="w-10 h-10 text-emerald-500/40" />
                </div>
                <h3 className="text-white font-semibold text-xl mb-2">No Timetable Uploaded</h3>
                <p className="text-gray-400 max-w-sm">
                  The timetable image for {selectedMosque.name} hasn't been uploaded yet.
                  Check back later or contact the mosque directly.
                </p>
                {selectedMosque.phone && (
                  <a
                    href={`tel:${selectedMosque.phone}`}
                    className="mt-6 badge-emerald cursor-pointer hover:bg-emerald-500/25 transition-colors"
                  >
                    {selectedMosque.phone}
                  </a>
                )}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
